// Stage 1 intake, built to be filled in under a minute while the customer is
// on the phone. Fees and final counts are deliberately absent: they belong to
// stage 2, on the onsite's own page, after the job.
//
// The same form edits stage 1 later, as long as the onsite is not canceled.

import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  createOnsite,
  getOnsite,
  updateOnsite,
  getCustomer,
  listCustomers,
  listFranchisees,
  listTestTypes,
} from '../api.js'
import { useApiForm } from '../useApiForm.js'
import {
  Button,
  ButtonLink,
  DateField,
  Input,
  Notice,
  Panel,
  Select,
} from '../components/ui/index.js'
import { fromInstant } from '../vocab.js'
import { fieldFactory } from '../formField.jsx'

const EMPTY = {
  customerId: '',
  franchiseeId: '',
  requestingContactName: '',
  requestingContactPhone: '',
  requestedDate: '',
  requestedTime: '',
  siteName: '',
  siteAddress: '',
  estimatedDonorCount: '',
  instructions: '',
  // For a TPA: one of its listed customers, or NOT_LISTED with a typed name.
  subCustomer: '',
  subCustomerName: '',
}

// The permanent last option of the TPA's customer list.
const NOT_LISTED = 'not-listed'

// The request time is a date plus a clock time, combined into one instant.
// Splitting the controls beats a raw datetime string nobody formats right on
// the phone.
function toInstant(date, time) {
  if (!date) return null
  return new Date(`${date}T${time || '09:00'}`).toISOString()
}

export default function OnsiteForm() {
  const { id } = useParams()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const [form, setForm] = useState(isEdit ? null : EMPTY)
  const [estimates, setEstimates] = useState({})
  const [customers, setCustomers] = useState([])
  // The listed customers of the chosen TPA; empty for any other customer.
  // The ref names the customer the list was last asked for, so a slow answer
  // for an earlier choice is dropped.
  const [subCustomers, setSubCustomers] = useState([])
  const subCustomerRequest = useRef(null)
  // On edit the customer is fixed, so whether it is a TPA comes from the
  // onsite itself; the active-customer list may no longer carry it.
  const [onsiteCustomerIsTpa, setOnsiteCustomerIsTpa] = useState(false)
  const [franchisees, setFranchisees] = useState([])
  const [testTypes, setTestTypes] = useState([])
  const [loadError, setLoadError] = useState(null)
  const { saving, error, fieldErrors, submit } = useApiForm()

  useEffect(() => {
    Promise.all([listCustomers({ status: 'ACTIVE' }), listFranchisees(), listTestTypes()])
      .then(([customerData, franchiseeData, testData]) => {
        setCustomers(customerData.customers)
        setFranchisees(franchiseeData.franchisees)
        setTestTypes(testData.testTypes.filter((testType) => testType.isActive))
      })
      .catch((caught) => setLoadError(caught.message))
  }, [])

  useEffect(() => {
    if (!isEdit) return
    getOnsite(id)
      .then(({ onsite }) => {
        const requested = fromInstant(onsite.requestedAt)
        setForm({
          customerId: onsite.customerId,
          franchiseeId: onsite.franchiseeId ?? '',
          requestingContactName: onsite.requestingContactName ?? '',
          requestingContactPhone: onsite.requestingContactPhone ?? '',
          requestedDate: requested.date,
          requestedTime: requested.time,
          siteName: onsite.siteName ?? '',
          siteAddress: onsite.siteAddress ?? '',
          estimatedDonorCount: onsite.estimatedDonorCount ?? '',
          instructions: onsite.instructions ?? '',
          subCustomer: onsite.subCustomerId ?? (onsite.subCustomerName ? NOT_LISTED : ''),
          subCustomerName: onsite.subCustomerId ? '' : (onsite.subCustomerName ?? ''),
        })
        setOnsiteCustomerIsTpa(Boolean(onsite.customerIsTpa))
        if (onsite.customerIsTpa) loadSubCustomers(onsite.customerId)
        setEstimates(
          Object.fromEntries(
            onsite.requestedTests.map((line) => [line.testTypeId, line.estimatedQuantity ?? '']),
          ),
        )
      })
      .catch((caught) => setLoadError(caught.message))
  }, [id, isEdit])

  function change(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  function loadSubCustomers(customerId) {
    subCustomerRequest.current = customerId
    getCustomer(customerId)
      .then((data) => {
        if (subCustomerRequest.current !== customerId) return
        setSubCustomers(data.customer.subCustomers ?? [])
      })
      .catch((caught) => {
        if (subCustomerRequest.current !== customerId) return
        setLoadError(caught.message)
      })
  }

  // Changing the customer starts the TPA pick over, and fetches the list
  // when the new customer is a TPA.
  function chooseCustomer(customerId) {
    setForm((current) => ({ ...current, customerId, subCustomer: '', subCustomerName: '' }))
    setSubCustomers([])
    subCustomerRequest.current = customerId
    if (customers.find((customer) => customer.id === customerId)?.isTpa) {
      loadSubCustomers(customerId)
    }
  }

  async function onSubmit(event) {
    event.preventDefault()
    const requestedTests = Object.entries(estimates)
      .filter(([, quantity]) => quantity !== '' && Number(quantity) >= 0)
      .map(([testTypeId, quantity]) => ({
        testTypeId,
        estimatedQuantity: quantity === '' ? null : Number(quantity),
      }))

    const payload = {
      customerId: form.customerId,
      franchiseeId: form.franchiseeId || null,
      requestingContactName: form.requestingContactName || null,
      requestingContactPhone: form.requestingContactPhone || null,
      requestedAt: toInstant(form.requestedDate, form.requestedTime),
      siteName: form.siteName || null,
      siteAddress: form.siteAddress || null,
      estimatedDonorCount:
        form.estimatedDonorCount === '' ? null : Number(form.estimatedDonorCount),
      instructions: form.instructions || null,
      subCustomerId: form.subCustomer && form.subCustomer !== NOT_LISTED ? form.subCustomer : null,
      subCustomerName: form.subCustomer === NOT_LISTED ? form.subCustomerName.trim() || null : null,
      requestedTests,
    }

    const result = await submit(() => (isEdit ? updateOnsite(id, payload) : createOnsite(payload)))
    if (result) navigate(`/onsites/${result.onsite.id}`)
  }

  if (loadError) {
    return <Notice tone="bad">{loadError}</Notice>
  }
  if (!form) return <div className="center-note">Loading</div>

  const field = fieldFactory({ form, change, fieldErrors })
  const isTpa = isEdit
    ? onsiteCustomerIsTpa
    : Boolean(customers.find((customer) => customer.id === form.customerId)?.isTpa)
  // Active customers only, plus the one already on the onsite, whatever its
  // status now, so an edit keeps showing what it has.
  const offeredSubCustomers = subCustomers.filter(
    (sub) => sub.status === 'ACTIVE' || sub.id === form.subCustomer,
  )
  const tpaCustomerMissing =
    isTpa &&
    (!form.subCustomer || (form.subCustomer === NOT_LISTED && !form.subCustomerName.trim()))

  return (
    <div className="form-page">
      <header className="page-head">
        <h1>{isEdit ? 'Edit the request' : 'New onsite'}</h1>
      </header>

      <Panel>
        <form onSubmit={onSubmit} noValidate>
          {error ? <Notice tone="bad">{error}</Notice> : null}

          <div className="formgrid">
            <Select
              id="customerId"
              className="span-2"
              label="Customer"
              value={form.customerId}
              onChange={chooseCustomer}
              error={fieldErrors.customerId}
              disabled={isEdit}
              placeholder="Choose a customer"
              options={[
                { value: '', label: 'Choose a customer' },
                ...customers.map((customer) => ({
                  value: customer.id,
                  label: customer.isTpa ? `${customer.name} (TPA)` : customer.name,
                })),
              ]}
            />

            {isTpa ? (
              <Select
                id="subCustomerId"
                className="span-2"
                label="Their customer"
                value={form.subCustomer}
                onChange={(subCustomer) => change('subCustomer', subCustomer)}
                error={fieldErrors.subCustomerId}
                help="The customer this TPA is booking the onsite for."
                options={[
                  { value: '', label: 'Choose their customer' },
                  ...offeredSubCustomers.map((sub) => ({ value: sub.id, label: sub.name })),
                  { value: NOT_LISTED, label: 'Customer not listed' },
                ]}
              />
            ) : null}
            {isTpa && form.subCustomer === NOT_LISTED
              ? field('subCustomerName', 'Customer name', {
                  wide: true,
                  help: 'As the TPA gave it. It is kept on this onsite only.',
                })
              : null}

            {field('requestingContactName', 'Who called')}
            {field('requestingContactPhone', 'Callback number')}

            <DateField
              id="requested-date"
              label="Requested date"
              value={form.requestedDate}
              onChange={(value) => change('requestedDate', value)}
            />
            {field('requestedTime', 'Time', {
              type: 'time',
              help: 'Defaults to 9:00 if left blank.',
            })}

            {field('siteName', 'Site or facility', { wide: true })}
            {field('siteAddress', 'Site address', {
              wide: true,
              help: 'Mileage is measured from the collector home base to here.',
            })}

            <Select
              id="franchiseeId"
              label="Dispatched to"
              value={form.franchiseeId}
              onChange={(franchiseeId) => change('franchiseeId', franchiseeId)}
              help="Their home base address is copied onto the job for mileage."
              options={[
                { value: '', label: 'Not assigned yet' },
                ...franchisees.map((franchisee) => ({
                  value: franchisee.id,
                  label: franchisee.name,
                })),
              ]}
            />
            {field('estimatedDonorCount', 'Estimated donors', { type: 'number', min: '0' })}

            <Input
              id="instructions"
              className="span-2"
              label="Special instructions"
              multiline
              value={form.instructions}
              onChange={(event) => change('instructions', event.target.value)}
            />
          </div>

          <div className="form-section">
            <h2>Requested tests</h2>
            <p className="support">
              Estimates from the call. What was actually performed is recorded in the completed
              on-site summary and can differ.
            </p>
          </div>

          <div className="rows">
            {testTypes.map((testType) => (
              <div key={testType.id} className="row">
                <div className="row-main">
                  <div className="row-primary">{testType.name}</div>
                </div>
                <div className="row-trail">
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    className="qty"
                    value={estimates[testType.id] ?? ''}
                    aria-label={`Estimated ${testType.name}`}
                    onChange={(event) =>
                      setEstimates((current) => ({ ...current, [testType.id]: event.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="btnrow">
            <ButtonLink to={isEdit ? `/onsites/${id}` : '/onsites'}>Cancel</ButtonLink>
            <span className="spacer" />
            <Button
              type="submit"
              variant="primary"
              block
              disabled={saving || !form.customerId || tpaCustomerMissing}
            >
              {saving ? 'Saving' : isEdit ? 'Save request' : 'Create onsite'}
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  )
}
